import { motion } from "framer-motion";
import { Plus } from "lucide-react";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";

const BADGE_IMG = "https://customer-assets-jt897jd0.emergentagent.net/job_compound-glow/artifacts/pxu6u8lx_Holographic%20Tatau%20Typography%20Edit%20%28A3%29.png";

export default function ProductCard({ product, index }) {
  const { addItem } = useCart();
  const soldOut = product.quantity <= 0;

  const handleAdd = () => {
    if (soldOut) return;
    addItem(product);
    toast.success(`${product.name} added to cart`, { description: "Opening your cart…" });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay: (index % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
      className="group relative border border-white/10 hover:border-[#00F0FF]/40 bg-gradient-to-b from-white/[0.02] to-transparent transition-colors flex flex-col"
      data-testid={`product-card-${product.id}`}
    >
      <div className="relative overflow-hidden bg-[#050507] aspect-[4/5] flex items-center justify-center">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,240,255,0.12),transparent_65%)] opacity-60 group-hover:opacity-100 transition-opacity" />
        <motion.img
          src={product.image}
          alt={product.name}
          className="relative h-full w-full object-cover vial-glow group-hover:scale-105 transition-transform duration-700"
          data-testid={`product-image-${product.id}`}
        />
        <img
          src={BADGE_IMG}
          alt="polyonpeps.com — thank you for your order"
          className="absolute bottom-1 right-1 w-[42%] pointer-events-none select-none mix-blend-screen opacity-90 z-10 drop-shadow-[0_0_10px_rgba(0,240,255,0.4)]"
          data-testid={`product-badge-${product.id}`}
        />
        {product.is_stack && (
          <span className="absolute top-3 left-3 text-[10px] tracking-[0.2em] uppercase bg-[#00F0FF] text-black px-2 py-1 font-bold font-mono2">Stack</span>
        )}
        {soldOut && (
          <span className="absolute top-3 right-3 text-[10px] tracking-[0.2em] uppercase bg-[#FF003C] text-white px-2 py-1 font-bold font-mono2">Sold Out</span>
        )}
      </div>

      <div className="p-5 flex flex-col flex-1">
        <h3 className="font-display font-extrabold text-base md:text-lg uppercase tracking-tight leading-tight" data-testid={`product-name-${product.id}`}>
          {product.name}
        </h3>
        <p className="mt-2 text-xs text-slate-400 font-mono2 leading-relaxed">{product.tagline}</p>

        {product.is_stack && product.ingredients?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {product.ingredients.map((ing) => (
              <span key={ing} className="text-[10px] uppercase tracking-wide border border-[#00F0FF]/30 text-[#00F0FF] px-2 py-0.5 font-mono2">{ing}</span>
            ))}
          </div>
        )}

        <p className="mt-3 text-[11px] text-slate-500 font-mono2 border-l-2 border-[#00F0FF]/40 pl-3">
          <span className="text-[#00F0FF]">DOSE:</span> {product.dosing}
        </p>

        <div className="mt-auto pt-5 flex items-center justify-between">
          <span className="font-display font-black text-xl text-white" data-testid={`product-price-${product.id}`}>${product.price.toFixed(2)}</span>
          <button
            onClick={handleAdd}
            disabled={soldOut}
            className="flex items-center gap-2 border border-[#00F0FF]/40 text-[#00F0FF] px-4 py-2 text-xs uppercase tracking-widest font-mono2 hover:bg-[#00F0FF] hover:text-black transition-colors disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-[#00F0FF]"
            data-testid={`add-to-cart-${product.id}`}
          >
            <Plus size={14} /> Add
          </button>
        </div>
      </div>
    </motion.div>
  );
}
